import { useEffect, useState } from 'react';
import { Disc3, Folder, FolderDown, MicVocal } from 'lucide-react';
import { API_BASE, asArray } from '../../../../config/api';
import { Section, SegmentedRow, TableRow, ToggleRow } from '../SettingsRows';

type RescanState = 'idle' | 'running' | 'done' | 'error';

interface LibraryFolder {
    path: string;
    track_count: number;
    tag: 'MUSIC' | 'PODCAST';
}

const RESCAN_LABEL: Record<RescanState, string> = {
    idle: 'Rescan',
    running: 'Scanning…',
    done: 'Scan started',
    error: 'Scan failed',
};

interface DeletedItem {
    type: 'album' | 'artist';
    id: number;
    title: string;
    deletedAt: string;
}

function timeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

interface ScanSettings {
    metadata_interval_days: number;
    art_interval_days: number;
}

// Label <-> days mapping for the two freshness settings. "Every scan" is
// 0 (the backend treats 0 as re-fetch always), "Never" for artwork is
// capped at 3650 days (~10 years) since the backend stores an interval,
// not a tri-state.
const METADATA_OPTIONS: Array<{ label: string; days: number }> = [
    { label: 'Every scan', days: 0 },
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
];

const ART_OPTIONS: Array<{ label: string; days: number }> = [
    { label: 'Every scan', days: 0 },
    { label: '1 year', days: 365 },
    { label: 'Never', days: 3650 },
];

function intervalLabel(days: number): string {
    return METADATA_OPTIONS.find((o) => o.days === days)?.label ?? `${days} days`;
}

function intervalValue(label: string): number {
    return METADATA_OPTIONS.find((o) => o.label === label)?.days ?? 0;
}

function artLabel(days: number): string {
    return ART_OPTIONS.find((o) => o.days === days)?.label ?? `${days} days`;
}

function artValue(label: string): number {
    return ART_OPTIONS.find((o) => o.label === label)?.days ?? 365;
}

export function LibraryTab(): React.JSX.Element {
    const [rescanState, setRescanState] = useState<Record<string, RescanState>>({});
    const [folders, setFolders] = useState<LibraryFolder[]>([]);
    const [foldersLoading, setFoldersLoading] = useState(true);
    const [scanSettings, setScanSettings] = useState<ScanSettings | null>(null);
    const [settingsSaved, setSettingsSaved] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [watch, setWatch] = useState(true);
    const [deletedItems, setDeletedItems] = useState<DeletedItem[]>([]);
    const [restoring, setRestoring] = useState<string | null>(null);

    useEffect(() => {
        fetch(`${API_BASE}/api/me`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : null))
            .then((me) => setIsAdmin(Boolean(me?.is_admin)))
            .catch(() => setIsAdmin(false));
    }, []);

    useEffect(() => {
        if (!isAdmin) {
            setFoldersLoading(false);
            return;
        }
        fetch(`${API_BASE}/api/settings?view=library-folders`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : []))
            .then((f) => setFolders(asArray<LibraryFolder>(f)))
            .catch(() => {})
            .finally(() => setFoldersLoading(false));
        fetch(`${API_BASE}/api/settings`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : null))
            .then((s) => {
                if (s) setScanSettings({ metadata_interval_days: s.metadata_interval_days, art_interval_days: s.art_interval_days });
            })
            .catch(() => {});
    }, [isAdmin]);

    const saveSettings = async (patch: Partial<ScanSettings>) => {
        if (!scanSettings) return;
        const next = { ...scanSettings, ...patch };
        setScanSettings(next);
        try {
            const response = await fetch(`${API_BASE}/api/settings`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(patch),
            });
            if (response.ok) {
                setSettingsSaved(true);
                setTimeout(() => setSettingsSaved(false), 2000);
            }
        } catch {
            // Leave the optimistic value on screen; it wasn't persisted.
        }
    };

    const loadDeleted = async () => {
        try {
            const [albumsRes, artistsRes] = await Promise.all([
                fetch(`${API_BASE}/api/album?deleted=true`, { credentials: 'include' }),
                fetch(`${API_BASE}/api/artist?deleted=true`, { credentials: 'include' }),
            ]);
            const albums = albumsRes.ok ? await albumsRes.json() : [];
            const artists = artistsRes.ok ? await artistsRes.json() : [];
            const items: DeletedItem[] = [
                ...asArray<any>(albums).map((a: any) => ({ type: 'album' as const, id: a.id, title: a.title, deletedAt: a.deleted_at })),
                ...asArray<any>(artists).map((a: any) => ({ type: 'artist' as const, id: a.id, title: a.title, deletedAt: a.deleted_at })),
            ].sort((a, b) => new Date(b.deletedAt).getTime() - new Date(a.deletedAt).getTime());
            setDeletedItems(items);
        } catch {
            // Leave whatever was already loaded - this section just won't refresh this time.
        }
    };

    useEffect(() => {
        loadDeleted();
    }, []);

    const handleRestore = async (item: DeletedItem) => {
        const key = `${item.type}-${item.id}`;
        setRestoring(key);
        try {
            const response = await fetch(`${API_BASE}/api/${item.type}/restore?id=${item.id}`, {
                method: 'POST',
                credentials: 'include',
            });
            if (response.ok) {
                setDeletedItems((prev) => prev.filter((i) => !(i.type === item.type && i.id === item.id)));
            }
        } finally {
            setRestoring(null);
        }
    };

    const handleRescan = async (id: string) => {
        if (rescanState[id] === 'running') return;
        setRescanState((s) => ({ ...s, [id]: 'running' }));
        try {
            const response = await fetch(`${API_BASE}/api/scan`, {
                method: 'POST',
                credentials: 'include',
            });
            setRescanState((s) => ({ ...s, [id]: response.ok ? 'done' : 'error' }));
        } catch {
            setRescanState((s) => ({ ...s, [id]: 'error' }));
        } finally {
            setTimeout(() => setRescanState((s) => ({ ...s, [id]: 'idle' })), 3000);
        }
    };

    return (
        <>
            <Section title="Folders">
                {foldersLoading ? (
                    <div className="settingsFoldersEmpty">Loading folders…</div>
                ) : folders.length === 0 ? (
                    <div className="settingsFoldersEmpty">No folders found in the library path</div>
                ) : (
                    folders.map((folder) => {
                        const key = folder.path;
                        return (
                            <TableRow
                                key={key}
                                icon={folder.tag === 'PODCAST' ? FolderDown : Folder}
                                main={folder.path}
                                sub={`${folder.track_count.toLocaleString()} track${folder.track_count === 1 ? '' : 's'}`}
                                tag={folder.tag}
                                tagTone={folder.tag === 'PODCAST' ? ('sage' as const) : ('blue' as const)}
                                actionLabel={RESCAN_LABEL[rescanState[key] ?? 'idle']}
                                onAction={() => handleRescan(key)}
                            />
                        );
                    })
                )}
            </Section>
            <Section title="Scanning">
                <ToggleRow
                    label="Watch for changes"
                    desc="Pick up new files the moment they land."
                    value={watch}
                    onChange={setWatch}
                />
                {isAdmin && scanSettings && (
                    <>
                        <SegmentedRow
                            label="Metadata refresh"
                            desc="Re-check artist bios and details against MusicBrainz."
                            options={['Every scan', '30 days', '90 days']}
                            value={intervalLabel(scanSettings.metadata_interval_days)}
                            onChange={(label) => saveSettings({ metadata_interval_days: intervalValue(label) })}
                        />
                        <SegmentedRow
                            label="Artwork refresh"
                            desc="Re-fetch artist photos and album covers."
                            options={['Every scan', '1 year', 'Never']}
                            value={artLabel(scanSettings.art_interval_days)}
                            onChange={(label) => saveSettings({ art_interval_days: artValue(label) })}
                        />
                        {settingsSaved && <div className="settingsSavedNote">Saved</div>}
                    </>
                )}
            </Section>
            {deletedItems.length > 0 && (
                <Section title="Recently deleted">
                    {deletedItems.map((item) => {
                        const key = `${item.type}-${item.id}`;
                        return (
                            <TableRow
                                key={key}
                                icon={item.type === 'album' ? Disc3 : MicVocal}
                                main={item.title}
                                sub={`${item.type === 'album' ? 'Album' : 'Artist'} · deleted ${timeAgo(item.deletedAt)}`}
                                actionLabel={restoring === key ? 'Restoring…' : 'Restore'}
                                onAction={() => handleRestore(item)}
                            />
                        );
                    })}
                </Section>
            )}
        </>
    );
}
